/* eslint-disable no-console */
import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { HiOutlineTrash, HiOutlineUserAdd, HiOutlineUsers } from 'react-icons/hi';

import { PanelLayout } from '../../components/PanelLayout';
import { useAuth } from '../../hooks/useAuth';
import { User } from '../../operations/auth/types';
import { deleteInvitedUser, getAllInvitedUsers } from '../../operations/users';
import { InvitedUser } from '../../operations/users/types';
import { Notifications } from './Notifications';
import { PasswordChange } from './PasswordChange';
import { Profile } from './Profile';
import {
  Container,
  DeleteButton,
  EmptyTeam,
  InviteButton,
  PageSubtitle,
  PageTitle,
  SectionDescription,
  SectionTitle,
  TabButton,
  TabContent,
  TabsContainer,
  TeamHeader,
  UserActions,
  UserAvatar,
  UserCard,
  UserDetails,
  UserEmail,
  UserInfo,
  UserName,
  UsersList,
  UserStatus,
} from './styles';
import { UserInviteModal } from '../Users/Modals/UserInviteModal';

type SelectedTab = 'profile' | 'password' | 'notifications' | 'team';

type InviteModalProps = {
  isOpen: boolean;
  data: User | null;
};

export const Config = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState<SelectedTab>('profile');
  const [users, setUsers] = useState<InvitedUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [inviteModal, setInviteModal] = useState<InviteModalProps>({
    isOpen: false,
    data: null,
  });

  useEffect(() => {
    if (selectedTab === 'team') {
      loadUsers();
    }
  }, [selectedTab]);

  async function loadUsers() {
    try {
      setIsLoadingUsers(true);
      const { invited_users } = await getAllInvitedUsers();
      setUsers(invited_users);
    } finally {
      setIsLoadingUsers(false);
    }
  }

  function openInviteModal(data: User | null = null) {
    setInviteModal({ isOpen: true, data });
  }

  function closeInviteModal() {
    setInviteModal({ isOpen: false, data: null });
  }

  async function handleDeleteUser(id: number, name: string) {
    Modal.confirm({
      title: 'Excluir usuário',
      content: `Tem certeza de que deseja excluir ${name}? Esta ação não poderá ser desfeita.`,
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteInvitedUser(id);
          setUsers(users.filter((u) => u.id !== id));
        } catch (error) {
          console.log(error);
        }
      },
    });
  }

  function getUserInitials(name: string) {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  const renderTeamContent = () => {
    if (isLoadingUsers) {
      return (
        <UsersList>
          {[1, 2, 3].map((i) => (
            <UserCard key={i} style={{ opacity: 0.5 }}>
              <UserInfo>
                <UserAvatar style={{ background: '#e0e0e0' }} />
                <UserDetails>
                  <UserName style={{ background: '#e0e0e0', width: 120, height: 14, borderRadius: 4 }} />
                  <UserEmail style={{ background: '#e0e0e0', width: 180, height: 12, borderRadius: 4, marginTop: 4 }} />
                </UserDetails>
              </UserInfo>
            </UserCard>
          ))}
        </UsersList>
      );
    }

    if (users.length === 0) {
      return (
        <EmptyTeam>
          <HiOutlineUsers />
          <p>Nenhum usuário convidado ainda.</p>
        </EmptyTeam>
      );
    }

    return (
      <UsersList>
        {users.map((u) => (
          <UserCard key={u.id}>
            <UserInfo>
              <UserAvatar>{getUserInitials(u.name)}</UserAvatar>
              <UserDetails>
                <UserName>{u.name}</UserName>
                <UserEmail>{u.email}</UserEmail>
              </UserDetails>
            </UserInfo>
            <UserActions>
              <UserStatus $isActive={!!u.actived_at}>
                {u.actived_at ? 'Ativo' : 'Convidado'}
              </UserStatus>
              <DeleteButton
                onClick={() => handleDeleteUser(u.id, u.name)}
                title="Excluir usuário"
              >
                <HiOutlineTrash />
              </DeleteButton>
            </UserActions>
          </UserCard>
        ))}
      </UsersList>
    );
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'profile':
        return <Profile />;
      case 'password':
        return <PasswordChange />;
      case 'notifications':
        return <Notifications />;
      case 'team':
        return (
          <>
            <TeamHeader>
              <div>
                <SectionTitle>Equipe</SectionTitle>
                <SectionDescription>
                  Gerencie os membros da sua equipe que têm acesso ao painel.
                </SectionDescription>
              </div>
              <InviteButton onClick={() => openInviteModal()}>
                <HiOutlineUserAdd />
                Convidar
              </InviteButton>
            </TeamHeader>
            {renderTeamContent()}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <PanelLayout>
      <Container>
        <PageTitle>Configurações</PageTitle>
        <PageSubtitle>Gerencie seu perfil e equipe</PageSubtitle>

        <TabsContainer>
          <TabButton
            $isActive={selectedTab === 'profile'}
            onClick={() => setSelectedTab('profile')}
          >
            Perfil
          </TabButton>
          <TabButton
            $isActive={selectedTab === 'password'}
            onClick={() => setSelectedTab('password')}
          >
            Senha
          </TabButton>
          <TabButton
            $isActive={selectedTab === 'notifications'}
            onClick={() => setSelectedTab('notifications')}
          >
            Notificações
          </TabButton>
          <TabButton
            $isActive={selectedTab === 'team'}
            onClick={() => setSelectedTab('team')}
          >
            Equipe
          </TabButton>
        </TabsContainer>

        <TabContent>{renderTabContent()}</TabContent>
      </Container>

      <UserInviteModal
        isVisible={inviteModal.isOpen}
        data={inviteModal.data}
        closeModal={closeInviteModal}
        setUsers={setUsers}
      />
    </PanelLayout>
  );
};
